import React, { useState, useEffect, useMemo } from "react";
import { UserPlus, Search, X, Shield, Loader2, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { CategoryColors } from "@/lib/colors";
import { PageHeader, Card, EmptyState, Spinner, ErrorBanner } from "@/components/shared/ui";

const ROLES = ["super_admin", "structure", "electrical", "mechanic", "finishing"];

export default function Users() {
  const { t } = useLanguage();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [dialog, setDialog] = useState(null); // {mode: 'create'|'edit'|'view'|'delete', user}

  const roleLabel = (role) => (role === "super_admin" ? t("super_admin") : t(role));

  const fetchUsers = async () => {
    try {
      setUsers(await apiClient.get("/users"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const rolesOfUser = (u) => (Array.isArray(u.roles) && u.roles.length ? u.roles : [u.role]);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      const matchSearch =
        !search ||
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase());
      const matchRole = !roleFilter || rolesOfUser(u).includes(roleFilter);
      return matchSearch && matchRole;
    });
  }, [users, search, roleFilter]);

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title={t("user_management")}
        subtitle={`${users.length} ${t("users").toLowerCase()}`}
        actions={
          <button
            data-testid="add-user-btn"
            onClick={() => setDialog({ mode: "create" })}
            className="flex items-center gap-2 rounded-xl bg-[var(--brand)] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            <UserPlus className="h-4 w-4" /> {t("add_user")}
          </button>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            data-testid="users-search-input"
            className="h-11 flex-1 bg-transparent text-sm outline-none"
            placeholder={t("search_users")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search ? (
            <button onClick={() => setSearch("")}>
              <X className="h-4 w-4 text-slate-400" />
            </button>
          ) : null}
        </div>
        <select
          data-testid="users-role-filter"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none"
        >
          <option value="">{t("all_roles")}</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="py-20 text-center">
          <Spinner className="mx-auto h-8 w-8 text-[var(--brand)]" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Shield} title={t("no_users_found")} />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3 text-start">{t("name")}</th>
                <th className="px-5 py-3 text-start">{t("email")}</th>
                <th className="px-5 py-3 text-start">{t("roles")}</th>
                <th className="px-5 py-3 text-end">{t("view")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div
                        className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold"
                        style={{ backgroundColor: `${CategoryColors[u.role] || "#2563EB"}20`, color: CategoryColors[u.role] || "#2563EB" }}
                      >
                        {(u.name || "U")[0].toUpperCase()}
                      </div>
                      <span className="font-medium text-slate-900">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">{u.email}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {rolesOfUser(u).map((r) => (
                        <span
                          key={r}
                          className="rounded-md px-2 py-0.5 text-xs font-semibold"
                          style={{ backgroundColor: `${CategoryColors[r] || "#2563EB"}15`, color: CategoryColors[r] || "#2563EB" }}
                        >
                          {roleLabel(r)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`view-user-${u.id}`} onClick={() => setDialog({ mode: "view", user: u })} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" title={t("view")}>
                        <Eye className="h-4 w-4" />
                      </button>
                      <button data-testid={`edit-user-${u.id}`} onClick={() => setDialog({ mode: "edit", user: u })} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-[var(--brand)] hover:bg-slate-50" title={t("edit")}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      {u.id !== me?.id ? (
                        <button data-testid={`delete-user-${u.id}`} onClick={() => setDialog({ mode: "delete", user: u })} className="flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-500 hover:bg-red-100" title={t("delete")}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {dialog?.mode === "create" ? <UserFormDialog onClose={() => setDialog(null)} onDone={fetchUsers} /> : null}
      {dialog?.mode === "edit" ? <UserFormDialog user={dialog.user} onClose={() => setDialog(null)} onDone={fetchUsers} /> : null}
      {dialog?.mode === "view" ? <ViewUserDialog user={dialog.user} onClose={() => setDialog(null)} /> : null}
      {dialog?.mode === "delete" ? <DeleteUserDialog user={dialog.user} onClose={() => setDialog(null)} onDone={fetchUsers} /> : null}
    </div>
  );
}

const roleLabelStatic = (t, role) => (role === "super_admin" ? t("super_admin") : t(role));

function RolesPicker({ value, onChange }) {
  const { t } = useLanguage();
  const toggle = (r) => {
    onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {ROLES.map((r) => {
        const active = value.includes(r);
        return (
          <button
            key={r}
            type="button"
            data-testid={`role-chip-${r}`}
            onClick={() => toggle(r)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
              active ? "border-transparent text-white" : "border-slate-200 bg-slate-50 text-slate-500"
            }`}
            style={active ? { backgroundColor: CategoryColors[r] || "#2563EB" } : {}}
          >
            {roleLabelStatic(t, r)}
          </button>
        );
      })}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose}>
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputCls = "h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-[var(--brand)]";
const labelCls = "mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500";

function UserFormDialog({ user, onClose, onDone }) {
  const { t } = useLanguage();
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    password: "",
    roles: user ? (Array.isArray(user.roles) && user.roles.length ? user.roles : [user.role]) : [],
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isEdit && (!form.name.trim() || !form.email.trim() || !form.password.trim())) {
      setError(t("enter_email_password"));
      return;
    }
    if (isEdit && !form.name.trim()) {
      setError(`${t("name")} ${t("is_required")}`);
      return;
    }
    if (!form.roles.length) {
      setError(t("at_least_one_role"));
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, roles: form.roles };
        if (form.password.trim()) payload.password = form.password;
        await apiClient.put(`/users/${user.id}`, payload);
        toast.success(t("user_updated"));
      } else {
        await apiClient.post("/users", { name: form.name, email: form.email, password: form.password, roles: form.roles });
        toast.success(t("user_created"));
      }
      onDone();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isEdit ? t("edit_user") : t("create_user")} onClose={onClose}>
      <form onSubmit={submit}>
        <ErrorBanner message={error} />

        <label className={labelCls}>{t("name")}</label>
        <input data-testid="user-name-input" className={`${inputCls} mb-4`} placeholder={t("enter_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />

        <label className={labelCls}>{t("email")}</label>
        <input data-testid="user-email-input" type="email" disabled={isEdit} className={`${inputCls} mb-4 disabled:opacity-60`} placeholder={t("enter_email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />

        <label className={labelCls}>{isEdit ? t("new_password") : t("password")}</label>
        <input data-testid="user-password-input" type="text" className={`${inputCls} mb-1`} placeholder={isEdit ? t("leave_blank_password") : t("enter_password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        {isEdit ? <p className="mb-4 text-xs text-slate-400">{t("leave_blank_password")}</p> : <div className="mb-4" />}

        <label className={labelCls}>{t("roles")}</label>
        <p className="mb-2 text-xs text-slate-400">{t("select_roles")}</p>
        <div className="mb-6">
          <RolesPicker value={form.roles} onChange={(roles) => setForm({ ...form, roles })} />
        </div>

        <button data-testid="submit-user-btn" type="submit" disabled={saving} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[var(--brand)] text-sm font-semibold text-white disabled:opacity-70">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          {isEdit ? t("save") : t("create_user")}
        </button>
      </form>
    </Modal>
  );
}

function ViewUserDialog({ user, onClose }) {
  const { t } = useLanguage();
  const roles = Array.isArray(user.roles) && user.roles.length ? user.roles : [user.role];
  const Row = ({ label, children }) => (
    <div className="flex items-start justify-between gap-4 border-b border-slate-50 py-3 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-end text-sm font-semibold text-slate-900">{children}</span>
    </div>
  );
  return (
    <Modal title={t("user_info")} onClose={onClose}>
      <Row label={t("name")}>{user.name}</Row>
      <Row label={t("email")}>{user.email}</Row>
      <Row label={t("roles")}>
        <div className="flex flex-wrap justify-end gap-1">
          {roles.map((r) => (
            <span key={r} className="rounded-md px-2 py-0.5 text-xs font-semibold" style={{ backgroundColor: `${CategoryColors[r] || "#2563EB"}15`, color: CategoryColors[r] || "#2563EB" }}>
              {roleLabelStatic(t, r)}
            </span>
          ))}
        </div>
      </Row>
      <Row label={t("created_at_label")}>{user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</Row>
    </Modal>
  );
}

function DeleteUserDialog({ user, onClose, onDone }) {
  const { t } = useLanguage();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const confirm = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiClient.del(`/users/${user.id}`);
      toast.success(t("user_deleted"));
      onDone();
      onClose();
    } catch (e) {
      setError(e.message);
      setDeleting(false);
    }
  };
  return (
    <Modal title={t("delete_user")} onClose={onClose}>
      <ErrorBanner message={error} />
      <p className="mb-6 text-sm text-slate-500">{t("confirm_delete_user")}</p>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">{t("cancel")}</button>
        <button data-testid="confirm-delete-user-btn" onClick={confirm} disabled={deleting} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-red-500 py-2.5 text-sm font-semibold text-white disabled:opacity-70">
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {t("delete")}
        </button>
      </div>
    </Modal>
  );
}
